import { ContactForm } from "../components/ContactForm";
import { useState, type KeyboardEvent } from "react";
import { Link } from "react-router";
import { ADDON_CATALOG, EXTRA_SEAT_MONTHLY_CENTS, formatEuro, FOUNDER_SLOTS, SUBSCRIPTION_TIERS, SUPPORT_EMAIL, SUBSCRIPTION_BOOKABLE } from "@handvero/shared";
import { DemoPlayer, LineArtDefs } from "../components/Showcase";
import { PhoneRow, ScreenTour } from "../components/Screens";
import { Page, PageHead } from "../components/ui";
import { useReveal, useStats } from "../hooks";

interface DemoNode {
  id: number;
  name: string;
  kids: DemoNode[];
}

/** Ordner-Demo zum Ausprobieren. Änderungen bleiben nur in dieser Ansicht. */
function FolderDemo() {
  const [uid, setUid] = useState(10);
  const [tree, setTree] = useState<DemoNode>({
    id: 1,
    name: "E-Check 2026",
    kids: [
      { id: 2, name: "01_Fotos", kids: [{ id: 3, name: "UV1", kids: [] }, { id: 4, name: "UV2", kids: [] }] },
      { id: 5, name: "02_Prüfbericht", kids: [] },
      { id: 6, name: "03_Absprachen", kids: [] },
      { id: 7, name: "04_Rechnung", kids: [] },
    ],
  });
  const [selected, setSelected] = useState(2);
  const [rename, setRename] = useState("01_Fotos");
  const [templates, setTemplates] = useState(["E-Check Standard"]);
  const [hint, setHint] = useState("");

  const map = (n: DemoNode, fn: (n: DemoNode) => DemoNode): DemoNode => fn({ ...n, kids: n.kids.map((k) => map(k, fn)) });
  const parentOf = (n: DemoNode, id: number): DemoNode | null => {
    for (const k of n.kids) {
      if (k.id === id) return n;
      const r = parentOf(k, id);
      if (r) return r;
    }
    return null;
  };

  function add() {
    const id = uid + 1;
    setUid(id);
    setTree(map(tree, (n) => (n.id === selected ? { ...n, kids: [...n.kids, { id, name: "Neuer Ordner", kids: [] }] } : n)));
    setSelected(id);
    setRename("Neuer Ordner");
  }
  function remove() {
    const parent = parentOf(tree, selected);
    if (!parent) return setHint("Der Projektordner selbst bleibt bestehen.");
    setTree(map(tree, (n) => (n.id === parent.id ? { ...n, kids: n.kids.filter((k) => k.id !== selected) } : n)));
    setSelected(parent.id);
    setRename(parent.name);
  }
  function doRename() {
    const v = rename.trim();
    if (v) setTree(map(tree, (n) => (n.id === selected ? { ...n, name: v } : n)));
  }
  function save() {
    setTemplates((t) => [...t, `${tree.name} (Vorlage ${t.length + 1})`]);
    setHint("Struktur als Vorlage gespeichert.");
  }

  const render = (n: DemoNode) => (
    <li key={n.id}>
      <button
        className={n.id === selected ? "sel" : undefined}
        onClick={() => {
          setSelected(n.id);
          setRename(n.name);
          setHint("");
        }}
      >
        {(n.kids.length ? "▾ " : "▪ ") + n.name}
      </button>
      {n.kids.length > 0 && <ul>{n.kids.map(render)}</ul>}
    </li>
  );

  return (
    <div className="demo" aria-label="Ordner-Demo">
      <div className="demo-tools">
        <button className="btn btn-ghost btn-small" onClick={add}>
          Unterordner anlegen
        </button>
        <button className="btn btn-ghost btn-small" onClick={remove}>
          Ordner entfernen
        </button>
        <button className="btn btn-primary btn-small" onClick={save}>
          Als Vorlage speichern
        </button>
      </div>
      <div className="demo-tree">
        <ul>{render(tree)}</ul>
      </div>
      <div className="demo-foot">
        <div className="rename">
          <label htmlFor="renameInput" className="hp">
            Ordnername
          </label>
          <input
            id="renameInput"
            placeholder="Ordner umbenennen"
            value={rename}
            onChange={(e) => setRename(e.target.value)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                e.preventDefault();
                doRename();
              }
            }}
          />
          <button className="btn btn-ghost btn-small" onClick={doRename}>
            Umbenennen
          </button>
        </div>
        {hint && <div>{hint}</div>}
        <div>Gespeicherte Vorlagen:</div>
        <div className="chips">
          {templates.map((t) => (
            <span className="chip" key={t}>
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function CtaBand() {
  return (
    <div className="cta-band reveal">
      <h2>Selbst ausprobieren</h2>
      <Link className="btn btn-primary" to="/registrieren">
        Kostenlos registrieren
      </Link>
    </div>
  );
}

export function Funktionen() {
  useReveal();
  const more: [string, string, string[]][] = [
    [
      "Fotos direkt in den richtigen Ordner",
      "Auf dem Handy den Ordner wählen und auslösen. Kein Sortieren am Abend, keine Bilder in privaten Chats.",
      ["Foto landet sofort im gewählten Projektordner", "Datum und Ersteller werden mitgespeichert", "Am PC sofort sichtbar, ohne Kabel und ohne Mail"],
    ],
    [
      "PDFs und Daten finden",
      "Prüfberichte, Angebote, Pläne und Datenblätter liegen beim Projekt. Auf der Baustelle öffnen Sie sie am Handy.",
      ["Suche über alle Projekte", "PDFs auf dem Handy ansehen", "Vorlagen für Angebot, Auftrag und Rechnung ablegen"],
    ],
    [
      "Arbeitsschutz und Nachweise",
      "Bald verfügbar, mit der Windows-Software: Unterweisungen, Gefährdungsbeurteilungen, Prüf- und Abnahmeprotokolle liegen beim Mitarbeiter oder beim Projekt. HandVero erinnert, bevor etwas fällig wird.",
      ["Unterweisungen mit Unterschrift auf dem Handy", "Fälligkeiten im Blick, mit Erinnerung per E-Mail und im Kalender", "Abnahmen und Mängellisten direkt mit Fotos"],
    ],
    [
      "Outlook, Kalender und Absprachen",
      "Termine und Mails hängen am Projekt. Was mit dem Kunden besprochen wurde, steht im Ordner und nicht nur im Kopf.",
      ["Outlook-Termine dem Projekt zuordnen", "E-Mails als Datei (.msg oder .eml) im Projekt ablegen", "Absprachen mit Datum und Teilnehmern festhalten"],
    ],
    [
      "Angebote, Aufträge und Rechnungen",
      "Vom Angebot bis zur Mahnung an einem Ort, verknüpft mit Ihren Kunden und Projekten. In allen bezahlten Paketen enthalten, für Firmen ohne Paket später als Zusatzmodul (noch nicht buchbar). HandVero ersetzt keine Steuerberatung, bitte lassen Sie die Einrichtung von Ihrer Steuerberatung prüfen.",
      [
        "Kundenkartei, jedes neue Projekt gehört zu einem Kunden",
        "Angebot, Auftrag, Rechnung und Gutschrift mit lückenlosen Nummern, auch für Kleinunternehmer",
        "Festgeschriebene Rechnungen lassen sich nicht mehr ändern, nur stornieren",
        "Zahlungserinnerung und Mahnungen mit Mahngebühren",
        "Versand per E-Mail, Link oder WhatsApp, vorher mit Vorschau",
        "PDF in Ihrer Farbe mit Ihrem Logo, auf Wunsch mit Überweisungs-QR-Code",
        "Ausgaben mit Beleg, Übersicht über Umsatz und offene Posten, Export für Ihre Steuerberatung",
        "Bankumsätze einlesen und Rechnungen zuordnen",
        "Umstieg: Kunden und Rechnungen aus einem anderen Programm übernehmen",
      ],
    ],
  ];
  const soon = new Set(["Arbeitsschutz und Nachweise", "Angebote, Aufträge und Rechnungen"]);
  return (
    <Page name="funktionen">
      <div className="wrap stack-lg">
        <PageHead
          eyebrow="Funktionen"
          title="Alles zum Auftrag in einer Struktur"
          lead="HandVero sorgt dafür, dass Fotos, Dokumente, Termine und Absprachen dort liegen, wo Sie sie suchen. Angebote und Rechnungen sind in den bezahlten Paketen enthalten."
        />
        <ScreenTour />
        <div className="feature">
          <div className="stack">
            <h2>Ordnerstrukturen, die Sie selbst bauen</h2>
            <ul className="checks">
              <li>Ordner per Klick anlegen, umbenennen und verschieben</li>
              <li>Bewährte Strukturen als Vorlage speichern, zum Beispiel für E-Check, Neubau oder Wartung</li>
              <li>Jeder Mitarbeiter sieht dieselbe Struktur am PC und auf dem Handy</li>
            </ul>
            <p className="hint" style={{ color: "var(--muted)", fontSize: ".9rem" }}>
              Probieren Sie es rechts aus. Die Änderungen bleiben nur in dieser Ansicht.
            </p>
          </div>
          <FolderDemo />
        </div>
        {more.map(([title, text, points]) => (
          <div className="feature" key={title}>
            <div className="stack">
              {soon.has(title) && <span className="tag">{title.startsWith("Angebote") ? "In Paketen enthalten" : "Bald verfügbar"}</span>}
              <h2>{title}</h2>
              <p className="lead">{text}</p>
            </div>
            <ul className="checks card reveal">
              {points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        ))}
        <CtaBand />
      </div>
    </Page>
  );
}

export function WebApp() {
  useReveal();
  return (
    <Page name="webapp">
      <LineArtDefs />
      <div className="wrap stack-lg">
        <PageHead
          eyebrow="Web-App für iPhone und Android"
          title="Die Baustelle ist mit dem Büro verbunden"
          lead="Die Web-App öffnet sich im Browser Ihres Smartphones und zeigt Ihre Projekte und Ordner. Eine Installation aus dem App Store ist nicht nötig."
        />
        <div className="grid-3 steps">
          {[
            ["Anmelden", "Auf dem Handy im Browser anmelden, nachdem Sie Ihr Firmenkonto angelegt haben."],
            ["Ordner wählen, Foto machen", "Das Foto landet direkt im gewählten Projektordner."],
            ["Zum Startbildschirm hinzufügen", "Ab jetzt startet HandVero wie jede andere App."],
          ].map(([t, p]) => (
            <div className="step reveal" key={t}>
              <div className="stack" style={{ gap: 6 }}>
                <h3>{t}</h3>
                <p style={{ color: "var(--muted)" }}>{p}</p>
              </div>
            </div>
          ))}
        </div>
        <PhoneRow />
        <DemoPlayer />
        <div className="grid-2">
          <div className="card reveal">
            <h3>Was Sie unterwegs tun können</h3>
            <ul className="checks">
              <li>Ordner wählen und Fotos direkt hineinschießen</li>
              <li>PDFs, Pläne und Berichte öffnen</li>
              <li>Absprachen notieren</li>
            </ul>
          </div>
          <div className="card reveal">
            <h3>Unterstützte Geräte</h3>
            <ul className="checks">
              <li>iPhone mit Safari</li>
              <li>Android mit Chrome</li>
              <li>Anmeldung mit Ihrem Konto, jeder Mitarbeiter mit seinem eigenen Zugang</li>
            </ul>
          </div>
        </div>
      </div>
    </Page>
  );
}

export function Preise() {
  useReveal();
  const stats = useStats();
  const left = stats?.founderSlotsLeft ?? FOUNDER_SLOTS;
  return (
    <Page name="preise">
      <div className="wrap stack-lg">
        <PageHead
          eyebrow="Preise"
          title="Kostenlos starten"
          lead={
            <>
              Die ersten {FOUNDER_SLOTS} Firmen nutzen HandVero mindestens 3 Jahre kostenlos mit bis zu 3 Mitarbeitern. Noch <b>{left}</b> Plätze frei.
            </>
          }
        />
        <div className="price-grid">
          <div className="price feat reveal">
            <span className="tag go">Startaktion</span>
            <h3>Für die ersten {FOUNDER_SLOTS} Firmen</h3>
            <div className="amount">
              0 € <small>für mindestens 3 Jahre</small>
            </div>
            <ul className="checks">
              <li>Bis zu 3 Mitarbeiter</li>
              <li>Mindestens 3 Jahre kostenlos</li>
              <li>Web-App auf dem Handy</li>
              <li>Windows-Software zum Herunterladen</li>
            </ul>
            <Link className="btn btn-primary" to="/registrieren">
              Startplatz sichern
            </Link>
          </div>
          <div className="price reveal">
            <span className="tag">Gratis</span>
            <h3>Für jede weitere Firma</h3>
            <div className="amount">
              0 € <small>ohne Laufzeitende</small>
            </div>
            <ul className="checks">
              <li>1 Mitarbeiter</li>
              <li>Web-App auf dem Handy</li>
              <li>Windows-Software zum Herunterladen</li>
              <li>Fotos, Ordner, Dokumente, Notizen und Termine</li>
              <li>Kostenlos, ohne Laufzeitende</li>
            </ul>
            <Link className="btn btn-ghost" to="/registrieren">
              Registrieren
            </Link>
          </div>
          {SUBSCRIPTION_TIERS.map((tier) => (
            <div className="price reveal" key={tier.id}>
              <span className="tag">{SUBSCRIPTION_BOOKABLE ? "Jetzt buchbar" : "Demnächst verfügbar"}</span>
              <h3>{tier.name}</h3>
              <div className="amount">
                {formatEuro(tier.monthlyCents)} <small>im Monat und Firma, netto</small>
              </div>
              <p className="small muted">
                oder {formatEuro(tier.yearlyCents)} im Jahr (zwei Monate gratis)
                {tier.extraSeatMonthlyCents > 0 ? `, dazu ${formatEuro(tier.extraSeatMonthlyCents)} im Monat je Mitarbeiter ab dem ${tier.includedSeats + 1}.` : ""}
              </p>
              <ul className="checks">
                <li>{tier.employeeLimit === 1 ? "1 Mitarbeiter" : tier.id === "business" ? `4 bis ${tier.employeeLimit} Mitarbeiter` : `Bis zu ${tier.employeeLimit} Mitarbeiter`}</li>
                <li>Windows-Software zum Herunterladen</li>
                <li>Angebote, Aufträge und Rechnungen inklusive</li>
                <li>Unterweisungen inklusive</li>
                {!SUBSCRIPTION_BOOKABLE && <li>Buchbar, sobald alles getestet ist</li>}
              </ul>
              {SUBSCRIPTION_BOOKABLE ? (
                <Link className="btn btn-primary" to="/kundenbereich/abo">
                  {tier.name} wählen
                </Link>
              ) : (
                <button className="btn btn-ghost" disabled>
                  Noch nicht buchbar
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="card reveal stack">
          <span className="tag">In Vorbereitung</span>
          <h3>Zusatzmodule</h3>
          <p>
            Zusätzliche Programmteile sind als eigene Zusatzabos geplant. Sie sind noch nicht buchbar, vorher entstehen keine Kosten. Die Preise sind
            geplant und gelten erst mit dem Start der Buchung. Alle Preise netto, jedes Modul einzeln und monatlich kündbar.
          </p>
          <ul className="addon-list">
            {ADDON_CATALOG.map((a) => (
              <li key={a.id}>
                <b>{a.name}</b>
                <span>
                  {formatEuro(a.monthlyCents)} im Monat oder {formatEuro(a.yearlyCents)} im Jahr
                </span>
                <small>
                  {a.text}
                  {a.requires ? " Nur zusammen mit „Rechnungen und Belege“." : ""}
                </small>
              </li>
            ))}
          </ul>
          <p className="muted-s">Weitere Mitarbeiter über die enthaltenen Plätze hinaus: {formatEuro(EXTRA_SEAT_MONTHLY_CENTS)} pro Person und Monat.</p>
        </div>
        <div className="faq stack">
          <h2>Häufige Fragen</h2>
          <div>
            <details>
              <summary>Wo liegen meine Daten?</summary>
              <p>In einem Rechenzentrum in Deutschland. Die Übertragung ist verschlüsselt. In der Startphase sind Sie selbst dafür verantwortlich, wichtige Daten zusätzlich zu sichern. Wir sagen derzeit keine automatische Datensicherung zu.</p>
            </details>
            <details>
              <summary>Was brauche ich für die Software?</summary>
              <p>Einen aktuellen Browser am PC oder Handy (iPhone oder Android) und eine Internetverbindung. Für die Windows-Software einen PC mit Windows 10 oder 11.</p>
            </details>
            <details>
              <summary>Wo finde ich die Windows-Software?</summary>
              <p>Nach der Registrierung und Bestätigung Ihrer E-Mail-Adresse im Kundenbereich unter „HandVero für Windows“. Protokolle, Online-Schulungen und das Zeichenprogramm sind in Arbeit. Wir informieren registrierte Firmen per E-Mail, sobald es Neuigkeiten gibt.</p>
            </details>
            <details>
              <summary>Was passiert nach den 3 Jahren der Startaktion?</summary>
              <p>Wir informieren Sie rechtzeitig. Ohne Ihre Zustimmung entstehen keine Kosten.</p>
            </details>
            <details>
              <summary>Warum wird meine Registrierung geprüft?</summary>
              <p>Wir prüfen jede Anmeldung automatisch und lassen keine Wegwerf-Adressen zu. So schützen wir Ihre Daten und unsere Kunden.</p>
            </details>
          </div>
        </div>
      </div>
    </Page>
  );
}

export function UeberUns() {
  useReveal();
  return (
    <Page name="ueber">
      <div className="wrap stack-lg">
        <PageHead
          eyebrow="Über uns"
          title="Die Software, die das Handwerk verdient"
          lead="HandVero sitzt in Elterlein im Erzgebirge. Wir bauen eine Software, die Dokumentation im Handwerk so einfach macht wie ein Foto."
        />
        <div className="grid-2">
          <div className="card reveal">
            <h3>Wohin wir wollen</h3>
            <p>
              Jeder Betrieb soll Fotos, Protokolle, Unterweisungen und Termine an einem Ort haben. Ohne Papierstapel, ohne Suchen, auf jedem Gerät.
              Wir kennen den Alltag auf der Baustelle und bauen HandVero Schritt für Schritt mit den Betrieben, die es nutzen.
            </p>
          </div>
          <div className="card reveal">
            <h3>Was uns wichtig ist</h3>
            <ul className="checks">
              <li>Einfach zu bedienen, auch mit Arbeitshandschuhen</li>
              <li>Server in Deutschland</li>
              <li>So wenig fremde Dienste wie möglich</li>
            </ul>
          </div>
        </div>
      </div>
    </Page>
  );
}

export function Kontakt() {
  useReveal();
  return (
    <Page name="kontakt">
      <div className="wrap stack-lg">
        <PageHead
          eyebrow="Kontakt"
          title="Fragen oder Support"
          lead="Schreiben Sie uns über das Formular unten oder per E-Mail. Wir antworten Ihnen persönlich."
        />
        <div className="grid-2">
          <div className="card reveal">
            <h3>HandVero</h3>
            <dl className="kv">
              <dt>Adresse</dt>
              <dd>Hauptstraße 21, 09481 Elterlein</dd>
              <dt>E-Mail</dt>
              <dd>
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </dd>
            </dl>
          </div>
        </div>
        <ContactForm />
      </div>
    </Page>
  );
}

/** Datenschutz in einfacher Sprache, ergänzend zur Datenschutzerklärung. */
export function Datensicherheit() {
  useReveal();
  return (
    <Page name="daten">
      <div className="wrap stack-lg">
        <PageHead
          eyebrow="Ihre Daten"
          title="Was mit Ihren Daten passiert, ohne Fachchinesisch"
          lead="Ihre Fotos, Dokumente und Kundendaten gehören Ihnen. Hier steht kurz und klar, wo sie liegen und wer sie sehen kann."
        />
        <div className="grid-2">
          <div className="card reveal">
            <h3>Wo Ihre Daten liegen</h3>
            <p>Auf gemieteten Servern der Hetzner Online GmbH in einem Rechenzentrum in Deutschland. Nicht in einer amerikanischen Cloud.</p>
          </div>
          <div className="card reveal">
            <h3>Wer sie sehen kann</h3>
            <p>
              Ihre Mitarbeiter sehen, was Sie ihnen freigeben. Wir sehen in Ihr Konto nur, wenn Sie uns dafür ausdrücklich und zeitlich begrenzt die
              Erlaubnis geben, zum Beispiel für Hilfe bei einem Problem.
            </p>
          </div>
          <div className="card reveal">
            <h3>Kein Tracking</h3>
            <p>Wir setzen keine Werbe- und Analyse-Werkzeuge ein und laden keine Schriften von fremden Servern. Wir verkaufen keine Daten.</p>
          </div>
          <div className="card reveal">
            <h3>Unser Assistent Vero</h3>
            <p>Vero arbeitet auf unseren eigenen Servern. Ihre Eingaben gehen an keinen fremden KI-Dienst.</p>
          </div>
          <div className="card reveal">
            <h3>Was über Dritte läuft</h3>
            <p>
              E-Mails verschicken wir über ein Postfach bei Microsoft 365 (Rechenzentrum in der EU). Ihren Outlook-Kalender verbinden nur Sie selbst, und
              nur, wenn Sie es möchten.
            </p>
          </div>
          <div className="card reveal">
            <h3>Sicherung und Löschung</h3>
            <p>
              Der Server legt täglich eine Sicherung an. Zusagen können wir in der Startphase dafür nicht geben, bitte speichern Sie wichtige Unterlagen auch bei sich. Wenn Sie Ihr Konto löschen, löschen wir
              Ihre Daten, bis auf Unterlagen, die das Gesetz aufzubewahren verlangt, zum Beispiel gebuchte Belege.
            </p>
          </div>
        </div>
        <p className="reveal">
          Die vollständigen, rechtlich maßgeblichen Angaben finden Sie in der <Link to="/rechtliches#datenschutz">Datenschutzerklärung</Link>. Fragen
          beantworten wir gern über das <Link to="/kontakt">Kontaktformular</Link>.
        </p>
      </div>
    </Page>
  );
}

export function NotFound() {
  return (
    <Page name="404">
      <div className="wrap narrow stack">
        <span className="eyebrow">Fehler 404</span>
        <h1>Diese Seite gibt es nicht.</h1>
        <p className="lead">Vielleicht hat sich die Adresse geändert.</p>
        <Link className="btn btn-primary" to="/" style={{ justifySelf: "start" }}>
          Zur Startseite
        </Link>
      </div>
    </Page>
  );
}
